// Per-item Decoration section on PresentationDetailPage — PR #129
// frontend half of the FLAT (not tiered) per-item decoration model
// shipped by PR #127.
//
// Hierarchy: presentation_item → decorations (per location) → charges
// (per setup/run/fixed/other). Reps add the location, fill the
// artwork detail (imprint_type / logo_name / logo_size / etc.), upload
// the logo file (R2 presigned PUT), then stack the cost+sell+margin
// charges. Backend stores cost+sell, DB generates margin.
//
// Tri-field auto-compute: any two of {cost, sell, margin} fills the
// third. Convention — Setup charges qty defaults to 1 (per-color per-
// item flat). Run charges qty defaults to the item's max-tier quantity
// (per-color per-piece). Fixed/other defaults to 1 and is freely edit-
// able. All artwork fields are open text per operator decision (matches
// commonsku, stays flexible across suppliers).

import { useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Check as CheckIcon,
  CopyPlus,
  ImagePlus,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ApiError, api } from "@/lib/api";
import { NewLocationForm } from "@/components/common/NewLocationForm";
import { cn } from "@/lib/utils";

export type DecoratorType = "supplier" | "third_party";
export type ChargeType =
  | "setup"
  | "run"
  | "digitizing"
  | "rush"
  | "screen"
  | "pms_match"
  | "other";

export interface DecorationCharge {
  id: string;
  decorationId: string;
  position: number;
  chargeType: ChargeType;
  label: string;
  quantity: number;
  cost: string;
  sellPrice: string;
  marginPct: string | null;
  displayToClient: boolean;
}

export interface Decoration {
  id: string;
  presentationItemId: string;
  position: number;
  location: string;
  decoratorType: DecoratorType;
  imprintType: string | null;
  proofRequired: string | null;
  logoName: string | null;
  logoSize: string | null;
  logoColor: string | null;
  repeatLogo: string | null;
  supplierNotes: string | null;
  logoFileId: string | null;
  charges: DecorationCharge[];
}

interface SectionProps {
  itemId: string;
  /** Largest tier quantity on the parent item — used as the default qty
   *  for new "run" charges (per-color per-piece). Falls back to 1 when
   *  the item has no tiers. */
  itemMaxTierQty: number;
  decorations: Decoration[];
  /** Other items in the SAME presentation — used by the "Copy to other
   *  items" picker (PR #131). Empty list hides the picker. */
  siblingItems?: Array<{ id: string; name: string }>;
  canEdit: boolean;
  onMutated: () => void;
}

export function DecorationSection(props: SectionProps) {
  const {
    itemId,
    itemMaxTierQty,
    decorations,
    siblingItems = [],
    canEdit,
    onMutated,
  } = props;
  const [error, setError] = useState<string | null>(null);

  const [namingLocation, setNamingLocation] = useState(false);
  const create = useMutation({
    mutationFn: async (location: string) =>
      api(`/presentation_items/${itemId}/decorations`, {
        method: "POST",
        body: JSON.stringify({ location }),
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      setNamingLocation(false);
      onMutated();
    },
  });

  const sorted = useMemo(
    () =>
      [...decorations].sort(
        (a, b) =>
          a.position - b.position ||
          a.location.localeCompare(b.location),
      ),
    [decorations],
  );

  return (
    <div className="border-t border-brown-50 px-3 py-3">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-brown-300">
          Imprint locations &amp; artwork
        </p>
        {canEdit && sorted.length > 0 && !namingLocation && (
          <button
            type="button"
            onClick={() => setNamingLocation(true)}
            className="text-xs font-semibold text-brown-400 underline hover:text-brown-500"
          >
            + Add another location
          </button>
        )}
      </div>
      {namingLocation && (
        <NewLocationForm
          pending={create.isPending}
          onAdd={(location) => create.mutate(location)}
          onCancel={() => setNamingLocation(false)}
        />
      )}
      {error && <p className="mb-2 text-xs text-rust-600">{error}</p>}
      {sorted.length === 0 ? (
        <div className="flex flex-col items-start gap-2 rounded-md border border-dashed border-brown-100 bg-cream-50 px-3 py-3">
          <p className="text-xs text-brown-400">
            Add an imprint location to record artwork detail
            (imprint type, logo size, proof requirements, etc.) for the
            supplier or decorator. Optional — Setup / Run / Fixed
            <strong> charges live in the Charges section above</strong>{" "}
            and don't require a location.
          </p>
          {canEdit && !namingLocation && (
            <button
              type="button"
              onClick={() => setNamingLocation(true)}
              className="rounded-md bg-peach-300 px-3 py-1.5 text-xs font-semibold text-brown-500 hover:bg-peach-200"
            >
              + Add imprint location
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {sorted.map((d) => (
            <DecorationRow
              key={d.id}
              decoration={d}
              itemMaxTierQty={itemMaxTierQty}
              siblingItems={siblingItems}
              canEdit={canEdit}
              onMutated={onMutated}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Decoration row ────────────────────────────────────────────────────

function DecorationRow(props: {
  decoration: Decoration;
  itemMaxTierQty: number;
  siblingItems: Array<{ id: string; name: string }>;
  canEdit: boolean;
  onMutated: () => void;
}) {
  const {
    decoration: d,
    itemMaxTierQty,
    siblingItems,
    canEdit,
    onMutated,
  } = props;
  const [editingLocation, setEditingLocation] = useState(false);
  const [locationDraft, setLocationDraft] = useState(d.location);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [showCopyPicker, setShowCopyPicker] = useState(false);
  const [copyTargets, setCopyTargets] = useState<Set<string>>(new Set());

  const copyTo = useMutation({
    mutationFn: async (targetItemIds: string[]) =>
      api<{ new_decoration_ids: string[] }>(
        `/presentation_item_decorations/${d.id}/copy-to`,
        {
          method: "POST",
          body: JSON.stringify({ target_item_ids: targetItemIds }),
        },
      ),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      setShowCopyPicker(false);
      setCopyTargets(new Set());
      onMutated();
    },
  });

  const patch = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      api(`/presentation_item_decorations/${d.id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      setEditingLocation(false);
      onMutated();
    },
  });

  const del = useMutation({
    mutationFn: async () =>
      api(`/presentation_item_decorations/${d.id}`, { method: "DELETE" }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
  });

  return (
    <div className="rounded-md border border-peach-200 bg-white p-3">
      {/* Header row */}
      <div className="flex flex-wrap items-center gap-2">
        {editingLocation ? (
          <>
            <Input
              type="text"
              value={locationDraft}
              onChange={(e) => setLocationDraft(e.target.value)}
              autoFocus
              maxLength={200}
              className="h-8 w-48 px-2 text-xs"
              placeholder="e.g. Front Center, Left Sleeve"
            />
            <button
              type="button"
              onClick={() =>
                patch.mutate({ location: locationDraft.trim() || d.location })
              }
              disabled={patch.isPending}
              className="rounded-md bg-peach-400 px-2 py-1 text-xs font-semibold text-white hover:bg-peach-500 disabled:opacity-50"
            >
              {patch.isPending ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditingLocation(false);
                setLocationDraft(d.location);
              }}
              className="text-xs font-semibold text-brown-300 hover:text-brown-500"
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => {
              if (!canEdit) return;
              setLocationDraft(d.location);
              setEditingLocation(true);
            }}
            disabled={!canEdit}
            className={cn(
              "flex items-center gap-1.5 text-sm font-semibold text-brown-500",
              canEdit && "rounded-md p-1 -m-1 hover:bg-cream-50",
            )}
          >
            {d.location}
            {canEdit && <Pencil className="h-3 w-3 text-brown-300" />}
          </button>
        )}
        {canEdit && !editingLocation && (
          <DecoratorTypeToggle
            value={d.decoratorType}
            onChange={(v) => patch.mutate({ decorator_type: v })}
            disabled={patch.isPending}
          />
        )}
        {!canEdit && (
          <span className="rounded-full bg-cream-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brown-400">
            {d.decoratorType === "supplier" ? "Supplier" : "Third-party"}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {canEdit && siblingItems.length > 0 && !confirmingDelete && !showCopyPicker && (
            <button
              type="button"
              onClick={() => {
                setShowCopyPicker(true);
                setError(null);
              }}
              className="inline-flex items-center gap-1 rounded-md border border-peach-300 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brown-500 hover:bg-peach-50"
              title="Duplicate this decoration onto other items in the same presentation"
            >
              <CopyPlus className="h-3 w-3" />
              Copy to items
            </button>
          )}
          {canEdit && !confirmingDelete && (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="rounded-md p-1 text-brown-300 hover:bg-rust-50 hover:text-rust-500"
              aria-label="Remove decoration"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          {canEdit && confirmingDelete && (
            <>
              <button
                type="button"
                onClick={() => del.mutate()}
                disabled={del.isPending}
                className="rounded-md bg-ink px-2 py-1 text-xs font-semibold text-white hover:bg-ink-700 disabled:opacity-50"
              >
                {del.isPending ? "Removing…" : "Confirm delete"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                disabled={del.isPending}
                className="text-xs font-semibold text-brown-300 hover:text-brown-500"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Copy-to-items picker — opens when "Copy to items" is clicked */}
      {canEdit && showCopyPicker && (
        <div className="mt-3 rounded-md border border-peach-300 bg-peach-50/40 p-3">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-brown-300">
            Copy this decoration to which other items?
          </p>
          <ul className="flex flex-col gap-1">
            {siblingItems.map((sib) => (
              <li key={sib.id}>
                <label className="flex items-center gap-2 text-xs text-brown-500">
                  <input
                    type="checkbox"
                    checked={copyTargets.has(sib.id)}
                    onChange={(e) => {
                      const next = new Set(copyTargets);
                      if (e.target.checked) next.add(sib.id);
                      else next.delete(sib.id);
                      setCopyTargets(next);
                    }}
                    className="h-4 w-4 rounded border-brown-200 text-ink underline underline-offset-4 focus:ring-peach-300"
                  />
                  <span className="truncate">{sib.name}</span>
                </label>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => copyTo.mutate(Array.from(copyTargets))}
              disabled={copyTo.isPending || copyTargets.size === 0}
              className="inline-flex items-center gap-1 rounded-md bg-peach-400 px-2.5 py-1 text-xs font-semibold text-white hover:bg-peach-500 disabled:opacity-50"
            >
              <CheckIcon className="h-3 w-3" />
              {copyTo.isPending
                ? "Copying…"
                : `Copy to ${copyTargets.size} item${
                    copyTargets.size === 1 ? "" : "s"
                  }`}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowCopyPicker(false);
                setCopyTargets(new Set());
              }}
              className="text-xs font-semibold text-brown-300 hover:text-brown-500"
            >
              Cancel
            </button>
            <span className="ml-auto text-[10px] text-brown-300">
              All artwork fields + charges duplicate. Original stays put.
            </span>
          </div>
        </div>
      )}

      {/* Artwork fields */}
      <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
        <ArtworkField
          label="Imprint type"
          value={d.imprintType}
          onSave={(v) => patch.mutate({ imprint_type: v })}
          placeholder="e.g. Silkscreen, Embroidery, Heat transfer"
          canEdit={canEdit}
        />
        <ArtworkField
          label="Logo name"
          value={d.logoName}
          onSave={(v) => patch.mutate({ logo_name: v })}
          placeholder="e.g. Acme primary mark"
          canEdit={canEdit}
        />
        <ArtworkField
          label="Logo size"
          value={d.logoSize}
          onSave={(v) => patch.mutate({ logo_size: v })}
          placeholder='e.g. 4" wide'
          canEdit={canEdit}
        />
        <ArtworkField
          label="Logo color"
          value={d.logoColor}
          onSave={(v) => patch.mutate({ logo_color: v })}
          placeholder="e.g. PMS 2935 C, White, 1-color"
          canEdit={canEdit}
        />
        <ArtworkField
          label="Re-Order?"
          value={d.repeatLogo}
          onSave={(v) => patch.mutate({ repeat_logo: v })}
          placeholder="No / Yes — PO #33252"
          canEdit={canEdit}
        />
        <ArtworkField
          label="Proof required"
          value={d.proofRequired}
          onSave={(v) => patch.mutate({ proof_required: v })}
          placeholder="Yes / No / Email proof"
          canEdit={canEdit}
        />
      </div>
      <ArtworkField
        label="Notes to supplier / decorator"
        value={d.supplierNotes}
        onSave={(v) => patch.mutate({ supplier_notes: v })}
        placeholder="Anything the decorator needs to know — bleed, special placement, color match notes…"
        canEdit={canEdit}
        multiline
      />

      <LogoUploader
        decorationId={d.id}
        logoFileId={d.logoFileId}
        canEdit={canEdit}
        onUploaded={(fileId) => patch.mutate({ logo_file_id: fileId })}
        onCleared={() => patch.mutate({ logo_file_id: null })}
      />

      {error && <p className="mt-2 text-xs text-rust-600">{error}</p>}

      <ChargesTable
        decorationId={d.id}
        charges={d.charges}
        itemMaxTierQty={itemMaxTierQty}
        canEdit={canEdit}
        onMutated={onMutated}
      />
    </div>
  );
}

// ─── Artwork field (inline editable open-text) ─────────────────────────

function ArtworkField(props: {
  label: string;
  value: string | null;
  onSave: (v: string | null) => void;
  placeholder: string;
  canEdit: boolean;
  multiline?: boolean;
}) {
  const { label, value, onSave, placeholder, canEdit, multiline } = props;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");

  if (editing) {
    return (
      <label className={cn("block", multiline && "md:col-span-2 mt-2")}>
        <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wide text-brown-300">
          {label}
        </span>
        {multiline ? (
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            maxLength={4000}
            autoFocus
            className="w-full rounded-md border border-peach-300 bg-cream-50/40 px-2 py-1.5 text-xs text-brown-500 focus:border-peach-400 focus:outline-none"
            placeholder={placeholder}
          />
        ) : (
          <Input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={multiline ? 4000 : 200}
            autoFocus
            placeholder={placeholder}
            className="h-8 px-2 text-xs"
          />
        )}
        <div className="mt-1 flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              const next = draft.trim();
              onSave(next === "" ? null : next);
              setEditing(false);
            }}
            className="rounded-md bg-peach-400 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-peach-500"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => {
              setDraft(value ?? "");
              setEditing(false);
            }}
            className="text-[10px] font-semibold text-brown-300 hover:text-brown-500"
          >
            Cancel
          </button>
        </div>
      </label>
    );
  }

  return (
    <div className={cn(multiline && "md:col-span-2 mt-2")}>
      <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wide text-brown-300">
        {label}
      </span>
      <button
        type="button"
        onClick={() => {
          if (!canEdit) return;
          setDraft(value ?? "");
          setEditing(true);
        }}
        disabled={!canEdit}
        className={cn(
          "block w-full rounded-md border border-brown-50 bg-cream-50/40 px-2 py-1 text-left text-xs",
          value ? "text-brown-500" : "text-brown-300 italic",
          canEdit && "hover:border-peach-300 hover:bg-cream-50",
        )}
      >
        {value ?? placeholder}
      </button>
    </div>
  );
}

// ─── Decorator type toggle ─────────────────────────────────────────────

function DecoratorTypeToggle(props: {
  value: DecoratorType;
  onChange: (v: DecoratorType) => void;
  disabled: boolean;
}) {
  const { value, onChange, disabled } = props;
  return (
    <div
      role="tablist"
      aria-label="Decorator source"
      className="inline-flex overflow-hidden rounded-full border border-brown-100 bg-cream-50 text-[10px] font-semibold uppercase tracking-wide"
    >
      <button
        type="button"
        role="tab"
        aria-selected={value === "supplier"}
        onClick={() => onChange("supplier")}
        disabled={disabled || value === "supplier"}
        className={cn(
          "px-2.5 py-0.5 transition-colors",
          value === "supplier"
            ? "bg-peach-400 text-white"
            : "text-brown-300 hover:bg-cream-100",
        )}
      >
        Supplier
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={value === "third_party"}
        onClick={() => onChange("third_party")}
        disabled={disabled || value === "third_party"}
        className={cn(
          "px-2.5 py-0.5 transition-colors",
          value === "third_party"
            ? "bg-peach-400 text-white"
            : "text-brown-300 hover:bg-cream-100",
        )}
      >
        Third-party
      </button>
    </div>
  );
}

// ─── Logo uploader (R2 presigned PUT) ──────────────────────────────────

function LogoUploader(props: {
  decorationId: string;
  logoFileId: string | null;
  canEdit: boolean;
  onUploaded: (fileId: string) => void;
  onCleared: () => void;
}) {
  const { decorationId, logoFileId, canEdit, onUploaded, onCleared } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const presign = await api<{
        file_id: string;
        upload_url: string;
        live: boolean;
      }>(`/presentation_item_decorations/${decorationId}/logo-upload`, {
        method: "POST",
        body: JSON.stringify({
          filename: file.name,
          mime_type: file.type || "application/octet-stream",
          size_bytes: file.size,
        }),
      });
      if (presign.live) {
        const put = await fetch(presign.upload_url, {
          method: "PUT",
          headers: {
            "Content-Type": file.type || "application/octet-stream",
          },
          body: file,
        });
        if (!put.ok) {
          throw new Error(
            `R2 upload failed: ${put.status} ${put.statusText}`,
          );
        }
      }
      return presign.file_id;
    },
    onError: (err) =>
      setUploadError(
        err instanceof Error ? err.message : "Logo upload failed.",
      ),
    onSuccess: (fileId) => {
      setUploadError(null);
      onUploaded(fileId);
      if (inputRef.current) inputRef.current.value = "";
    },
  });

  const onPick = (): void => {
    setUploadError(null);
    inputRef.current?.click();
  };

  return (
    <div className="mt-3">
      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-brown-300">
        Logo artwork
      </span>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml,application/pdf,.ai,.eps"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload.mutate(f);
        }}
      />
      {logoFileId ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-brown-50 bg-cream-50/40 px-2 py-1.5">
          <span className="text-xs text-brown-400">
            Logo on file (id: <span className="font-mono">{logoFileId.slice(0, 8)}…</span>)
          </span>
          {canEdit && (
            <>
              <button
                type="button"
                onClick={onPick}
                disabled={upload.isPending}
                className="text-[10px] font-semibold text-brown-400 underline hover:text-brown-500 disabled:opacity-50"
              >
                {upload.isPending ? "Replacing…" : "Replace"}
              </button>
              <button
                type="button"
                onClick={onCleared}
                className="text-[10px] font-semibold text-brown-300 underline hover:text-rust-500"
              >
                Remove
              </button>
            </>
          )}
        </div>
      ) : canEdit ? (
        <button
          type="button"
          onClick={onPick}
          disabled={upload.isPending}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-peach-300 bg-cream-50/40 px-3 py-3 text-xs font-semibold text-brown-400 hover:border-peach-400 hover:bg-cream-50 disabled:opacity-50"
        >
          <ImagePlus className="h-4 w-4" />
          {upload.isPending ? "Uploading…" : "Upload logo (PNG, JPG, SVG, PDF, AI, EPS)"}
        </button>
      ) : (
        <p className="text-xs italic text-brown-300">No logo on file</p>
      )}
      {uploadError && (
        <p className="mt-1 text-[10px] text-rust-600">{uploadError}</p>
      )}
    </div>
  );
}

// ─── Charges table ────────────────────────────────────────────────────

export const CHARGE_TYPE_LABELS: Record<ChargeType, string> = {
  setup: "Setup",
  run: "Run",
  digitizing: "Digitizing",
  rush: "Rush",
  screen: "Screen",
  pms_match: "PMS match",
  other: "Other",
};

function ChargesTable(props: {
  decorationId: string;
  charges: DecorationCharge[];
  itemMaxTierQty: number;
  canEdit: boolean;
  onMutated: () => void;
}) {
  const { decorationId, charges, itemMaxTierQty, canEdit, onMutated } = props;
  const [addingType, setAddingType] = useState<ChargeType | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: async (chargeId: string) =>
      api(`/presentation_item_decoration_charges/${chargeId}`, {
        method: "DELETE",
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
  });

  const toggleVisible = useMutation({
    mutationFn: async (args: { chargeId: string; visible: boolean }) =>
      api(`/presentation_item_decoration_charges/${args.chargeId}`, {
        method: "PATCH",
        body: JSON.stringify({ display_to_client: args.visible }),
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
  });

  const sorted = useMemo(
    () =>
      [...charges].sort(
        (a, b) => a.position - b.position || a.label.localeCompare(b.label),
      ),
    [charges],
  );

  return (
    <div className="mt-4 rounded-md border border-brown-50 bg-cream-50/40 p-2">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-brown-300">
          Charges
        </p>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            <ChargeAddButton type="setup" onClick={() => setAddingType("setup")} />
            <ChargeAddButton type="run" onClick={() => setAddingType("run")} />
            <ChargeAddButton type="other" onClick={() => setAddingType("other")} label="+ Fixed" />
          </div>
        )}
      </div>
      {error && <p className="mb-2 text-xs text-rust-600">{error}</p>}
      {sorted.length === 0 && addingType === null ? (
        <p className="text-xs italic text-brown-300">
          No charges yet — add Setup (per-color flat), Run (per-color per-piece), or Fixed (digitizing, rush, etc.).
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[11px] font-medium text-muted">
              <tr>
                <th className="py-1 pr-2">Type</th>
                <th className="py-1 pr-2">Label</th>
                <th className="py-1 pr-2">Qty</th>
                <th className="py-1 pr-2">Cost</th>
                <th className="py-1 pr-2">Sell</th>
                <th className="py-1 pr-2">Margin</th>
                <th className="py-1 pr-2">Total sell</th>
                <th className="py-1 pr-2">Visible</th>
                {canEdit && <th className="py-1 pr-1 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) =>
                editingId === c.id ? (
                  <ChargeEditRow
                    key={c.id}
                    charge={c}
                    onCancel={() => setEditingId(null)}
                    onSaved={() => {
                      setEditingId(null);
                      onMutated();
                    }}
                  />
                ) : (
                  <tr key={c.id} className="border-t border-brown-50/60">
                    <td className="py-1.5 pr-2">
                      <span className="rounded-full bg-peach-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brown-500">
                        {CHARGE_TYPE_LABELS[c.chargeType] ?? c.chargeType}
                      </span>
                    </td>
                    <td className="py-1.5 pr-2 text-brown-500">{c.label}</td>
                    <td className="py-1.5 pr-2 text-brown-400">
                      {c.quantity.toLocaleString()}
                    </td>
                    <td className="py-1.5 pr-2 text-brown-400">
                      ${Number(c.cost).toFixed(2)}
                    </td>
                    <td className="py-1.5 pr-2 font-semibold text-brown-500">
                      ${Number(c.sellPrice).toFixed(2)}
                    </td>
                    <td className="py-1.5 pr-2 text-brown-400">
                      {c.marginPct
                        ? `${Number(c.marginPct).toFixed(1)}%`
                        : "—"}
                    </td>
                    <td className="py-1.5 pr-2 font-semibold text-rust-500">
                      ${(Number(c.sellPrice) * c.quantity).toFixed(2)}
                    </td>
                    <td className="py-1.5 pr-2">
                      {canEdit ? (
                        <button
                          type="button"
                          onClick={() =>
                            toggleVisible.mutate({
                              chargeId: c.id,
                              visible: !c.displayToClient,
                            })
                          }
                          disabled={toggleVisible.isPending}
                          className={cn(
                            "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                            c.displayToClient
                              ? "bg-peach-100 text-brown-500 hover:bg-peach-200"
                              : "border border-rust-300 text-rust-500 hover:bg-rust-50",
                          )}
                        >
                          {c.displayToClient ? "Visible" : "Internal only"}
                        </button>
                      ) : (
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                            c.displayToClient
                              ? "bg-peach-100 text-brown-500"
                              : "border border-rust-300 text-rust-500",
                          )}
                        >
                          {c.displayToClient ? "Visible" : "Internal only"}
                        </span>
                      )}
                    </td>
                    {canEdit && (
                      <td className="py-1.5 pr-1">
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => setEditingId(c.id)}
                            className="rounded-md p-1 text-brown-300 hover:bg-cream-100 hover:text-brown-500"
                            aria-label="Edit charge"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => remove.mutate(c.id)}
                            disabled={remove.isPending}
                            className="rounded-md p-1 text-brown-300 hover:bg-rust-50 hover:text-rust-500"
                            aria-label="Remove charge"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
      {addingType !== null && (
        <ChargeAddRow
          decorationId={decorationId}
          initialType={addingType}
          itemMaxTierQty={itemMaxTierQty}
          onCancel={() => setAddingType(null)}
          onAdded={() => {
            setAddingType(null);
            onMutated();
          }}
        />
      )}
    </div>
  );
}

function ChargeAddButton(props: {
  type: ChargeType;
  onClick: () => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className="inline-flex items-center gap-1 rounded-md border border-peach-300 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brown-500 hover:bg-peach-50"
    >
      <Plus className="h-3 w-3" />
      {props.label ?? CHARGE_TYPE_LABELS[props.type]}
    </button>
  );
}

// ─── Charge add row + edit row (share tri-field auto-compute) ──────────

function ChargeAddRow(props: {
  decorationId: string;
  initialType: ChargeType;
  itemMaxTierQty: number;
  onCancel: () => void;
  onAdded: () => void;
}) {
  const { decorationId, initialType, itemMaxTierQty, onCancel, onAdded } =
    props;
  const defaultQty = initialType === "run" ? Math.max(itemMaxTierQty, 1) : 1;
  const [chargeType, setChargeType] = useState<ChargeType>(initialType);
  const [label, setLabel] = useState(suggestLabel(initialType));
  const [quantity, setQuantity] = useState(String(defaultQty));
  const [cost, setCost] = useState("");
  const [sell, setSell] = useState("");
  const [margin, setMargin] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      api(`/presentation_item_decorations/${decorationId}/charges`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => onAdded(),
  });

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    setError(null);
    const q = Number.parseInt(quantity, 10);
    if (!Number.isInteger(q) || q < 0) {
      setError("Quantity must be a non-negative whole number.");
      return;
    }
    const c = cost.trim();
    const s = sell.trim();
    if (!/^\d+(\.\d{1,2})?$/.test(c)) {
      setError("Cost must be numeric (e.g. 12.00).");
      return;
    }
    if (!/^\d+(\.\d{1,2})?$/.test(s)) {
      setError("Sell must be numeric (e.g. 20.00).");
      return;
    }
    if (label.trim() === "") {
      setError("Add a short label (e.g. 'Screen setup').");
      return;
    }
    create.mutate({
      charge_type: chargeType,
      label: label.trim(),
      quantity: q,
      cost: c,
      sell_price: s,
      display_to_client: true,
    });
  };

  return (
    <form
      onSubmit={submit}
      className="mt-3 rounded-md border border-peach-300/70 bg-white p-3"
    >
      <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
        <label className="block md:col-span-1">
          <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wide text-brown-300">
            Type
          </span>
          <select
            value={chargeType}
            onChange={(e) => {
              const next = e.target.value as ChargeType;
              setChargeType(next);
              setLabel(suggestLabel(next));
              if (next === "run") {
                setQuantity(String(Math.max(itemMaxTierQty, 1)));
              } else {
                setQuantity("1");
              }
            }}
            className="h-8 w-full rounded-md border border-brown-100 bg-white px-2 text-xs text-brown-500 focus:border-peach-400 focus:outline-none"
          >
            {(
              [
                "setup",
                "run",
                "digitizing",
                "rush",
                "screen",
                "pms_match",
                "other",
              ] as ChargeType[]
            ).map((t) => (
              <option key={t} value={t}>
                {CHARGE_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="block md:col-span-2">
          <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wide text-brown-300">
            Label
          </span>
          <Input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={200}
            placeholder="e.g. Front 1-color screen"
            className="h-8 px-2 text-xs"
          />
        </label>
        <NumberField
          label="Qty"
          value={quantity}
          onChange={setQuantity}
          placeholder="1"
        />
        <PriceTriField
          cost={cost}
          sell={sell}
          margin={margin}
          onCost={setCost}
          onSell={setSell}
          onMargin={setMargin}
        />
      </div>
      {error && <p className="mt-2 text-xs text-rust-600">{error}</p>}
      <div className="mt-3 flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={create.isPending}
        >
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={create.isPending}>
          {create.isPending ? "Adding…" : "Add charge"}
        </Button>
      </div>
    </form>
  );
}

function ChargeEditRow(props: {
  charge: DecorationCharge;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { charge, onCancel, onSaved } = props;
  const [chargeType, setChargeType] = useState<ChargeType>(charge.chargeType);
  const [label, setLabel] = useState(charge.label);
  const [quantity, setQuantity] = useState(String(charge.quantity));
  const [cost, setCost] = useState(charge.cost);
  const [sell, setSell] = useState(charge.sellPrice);
  const [margin, setMargin] = useState(
    charge.marginPct ? Number(charge.marginPct).toFixed(2) : "",
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      api(`/presentation_item_decoration_charges/${charge.id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => onSaved(),
  });

  const submit = (): void => {
    setError(null);
    const q = Number.parseInt(quantity, 10);
    if (!Number.isInteger(q) || q < 0) {
      setError("Quantity must be a non-negative whole number.");
      return;
    }
    const c = cost.trim();
    const s = sell.trim();
    if (!/^\d+(\.\d{1,2})?$/.test(c)) {
      setError("Cost must be numeric.");
      return;
    }
    if (!/^\d+(\.\d{1,2})?$/.test(s)) {
      setError("Sell must be numeric.");
      return;
    }
    const body: Record<string, unknown> = {};
    if (chargeType !== charge.chargeType) body.charge_type = chargeType;
    if (label.trim() !== charge.label) body.label = label.trim();
    if (q !== charge.quantity) body.quantity = q;
    if (c !== charge.cost) body.cost = c;
    if (s !== charge.sellPrice) body.sell_price = s;
    if (Object.keys(body).length === 0) {
      onCancel();
      return;
    }
    save.mutate(body);
  };

  return (
    <tr className="border-t border-brown-50/60 bg-peach-50/40">
      <td colSpan={9} className="p-2">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
          <label className="block md:col-span-1">
            <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wide text-brown-300">
              Type
            </span>
            <select
              value={chargeType}
              onChange={(e) => setChargeType(e.target.value as ChargeType)}
              className="h-8 w-full rounded-md border border-brown-100 bg-white px-2 text-xs text-brown-500 focus:border-peach-400 focus:outline-none"
            >
              {(
                [
                  "setup",
                  "run",
                  "digitizing",
                  "rush",
                  "screen",
                  "pms_match",
                  "other",
                ] as ChargeType[]
              ).map((t) => (
                <option key={t} value={t}>
                  {CHARGE_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </label>
          <label className="block md:col-span-2">
            <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wide text-brown-300">
              Label
            </span>
            <Input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={200}
              className="h-8 px-2 text-xs"
            />
          </label>
          <NumberField
            label="Qty"
            value={quantity}
            onChange={setQuantity}
            placeholder="1"
          />
          <PriceTriField
            cost={cost}
            sell={sell}
            margin={margin}
            onCost={setCost}
            onSell={setSell}
            onMargin={setMargin}
          />
        </div>
        {error && <p className="mt-2 text-xs text-rust-600">{error}</p>}
        <div className="mt-2 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={save.isPending}
            className="rounded-md px-2 py-1 text-xs font-semibold text-brown-300 hover:bg-cream-100 hover:text-brown-500"
          >
            <X className="inline h-3.5 w-3.5" /> Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={save.isPending}
            className="rounded-md bg-peach-400 px-2.5 py-1 text-xs font-semibold text-white hover:bg-peach-500 disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </td>
    </tr>
  );
}

// ─── Tri-field cost / sell / margin auto-compute ──────────────────────
//
// Rule: any two of {cost, sell, margin} fills the third. Edits to one
// field re-derive the field that wasn't last touched, leaving the other
// two untouched. Margin is (sell - cost) / sell * 100 — matches the
// backend GENERATED ALWAYS column on presentation_item_decoration_charges.
// `lastTouched` tracks the field the user edited most recently so we
// don't blow away the value they just typed.

export function PriceTriField(props: {
  cost: string;
  sell: string;
  margin: string;
  onCost: (v: string) => void;
  onSell: (v: string) => void;
  onMargin: (v: string) => void;
}) {
  const { cost, sell, margin, onCost, onSell, onMargin } = props;
  const [lastTouched, setLastTouched] = useState<
    "cost" | "sell" | "margin" | null
  >(null);

  function handleCost(v: string): void {
    onCost(v);
    setLastTouched("cost");
    const c = Number(v);
    if (!Number.isFinite(c)) return;
    // Re-derive margin if sell is set, else re-derive sell if margin is set.
    const s = Number(sell);
    if (Number.isFinite(s) && s > 0) {
      onMargin(((s - c) / s * 100).toFixed(2));
    } else if (margin !== "") {
      const m = Number(margin);
      if (Number.isFinite(m) && m < 100) {
        onSell((c / (1 - m / 100)).toFixed(2));
      }
    }
  }

  function handleSell(v: string): void {
    onSell(v);
    setLastTouched("sell");
    const s = Number(v);
    if (!Number.isFinite(s) || s === 0) return;
    const c = Number(cost);
    if (Number.isFinite(c) && cost !== "") {
      onMargin(((s - c) / s * 100).toFixed(2));
    } else if (margin !== "") {
      const m = Number(margin);
      if (Number.isFinite(m) && m < 100) {
        onCost((s * (1 - m / 100)).toFixed(2));
      }
    }
  }

  function handleMargin(v: string): void {
    onMargin(v);
    setLastTouched("margin");
    const m = Number(v);
    if (!Number.isFinite(m) || m >= 100) return;
    const c = Number(cost);
    if (Number.isFinite(c) && cost !== "" && lastTouched !== "sell") {
      onSell((c / (1 - m / 100)).toFixed(2));
    } else {
      const s = Number(sell);
      if (Number.isFinite(s) && sell !== "") {
        onCost((s * (1 - m / 100)).toFixed(2));
      }
    }
  }

  return (
    <>
      <NumberField
        label="Cost"
        value={cost}
        onChange={handleCost}
        placeholder="12.00"
      />
      <NumberField
        label="Sell"
        value={sell}
        onChange={handleSell}
        placeholder="20.00"
      />
      <NumberField
        label="Margin %"
        value={margin}
        onChange={handleMargin}
        placeholder="40.00"
      />
    </>
  );
}

export function NumberField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <label className="block">
      <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wide text-brown-300">
        {props.label}
      </span>
      <Input
        type="text"
        inputMode="decimal"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        className="h-8 px-2 text-xs"
      />
    </label>
  );
}

export function suggestLabel(t: ChargeType): string {
  switch (t) {
    case "setup":
      return "Screen setup";
    case "run":
      return "Imprint";
    case "digitizing":
      return "Digitizing";
    case "rush":
      return "Rush";
    case "screen":
      return "Screen charge";
    case "pms_match":
      return "PMS match";
    case "other":
      return "Other charge";
  }
}

export function extractError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return body?.message ?? body?.error ?? `Request failed (${err.status}).`;
  }
  return "Request failed. Try again or check your connection.";
}
